import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import {
  LinkIcon, ArrowLeft, Copy, Check, AlertCircle,
  DollarSign, Send,
} from 'lucide-react';
import {
  callAdminListUsers, callAdminGeneratePaymentLink,
} from '../../services/admin';
import { callGetPlanCatalog } from '../../services/functions';

type UserOption = { uid: string; email: string };
type LinkMode = 'subscription' | 'custom';

const PRODUCTS = [
  { id: 'vetaxis', name: 'VetAxis' },
  { id: 'staffaxis', name: 'StaffAxis' },
  { id: 'noteaxis', name: 'NoteAxis' },
  { id: 'suite', name: 'Suite Bundle' },
];

const AdminPaymentLinks: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [plans, setPlans] = useState<Record<string, Array<{ id: string; name: string; price: number }>>>({});
  const [loading, setLoading] = useState(true);

  // Form state
  const [linkMode, setLinkMode] = useState<LinkMode>('subscription');
  const [selectedUid, setSelectedUid] = useState(searchParams.get('uid') || '');
  const [selectedProduct, setSelectedProduct] = useState('vetaxis');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [itemName, setItemName] = useState('');
  const [notes, setNotes] = useState('');

  // Result state
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [generatedAmount, setGeneratedAmount] = useState<number>(0);
  const [generatedMode, setGeneratedMode] = useState<LinkMode>('subscription');
  const [generatedTargeted, setGeneratedTargeted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [{ users: userList }, catalog] = await Promise.all([
          callAdminListUsers({ limit: 200 }),
          callGetPlanCatalog(),
        ]);
        if (mounted) {
          setUsers(userList.map((u: any) => ({ uid: u.uid, email: u.email })));
          const planMap: Record<string, Array<{ id: string; name: string; price: number }>> = {};
          for (const key of ['vetaxis', 'staffaxis', 'noteaxis'] as const) {
            planMap[key] = (catalog[key] || []).map((p: any) => ({ id: p.id, name: p.name, price: p.price || 0 }));
          }
          planMap.suite = (catalog.suite || []).map((p: any) => ({ id: p.id, name: p.name, price: p.price || 0 }));
          setPlans(planMap);

          const defaultPlans = planMap[selectedProduct];
          if (defaultPlans?.length) setSelectedPlan(defaultPlans[0].id);
        }
      } catch {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const productPlans = plans[selectedProduct];
    if (productPlans?.length) {
      setSelectedPlan(productPlans[0].id);
    } else {
      setSelectedPlan('');
    }
  }, [selectedProduct, plans]);

  const customAmountNum = Number(customAmount);
  const hasValidCustomAmount = Number.isFinite(customAmountNum) && customAmountNum > 0;
  const canGenerate = linkMode === 'subscription'
    ? Boolean(selectedUid && selectedProduct && selectedPlan)
    : hasValidCustomAmount;

  const handleGenerate = async () => {
    if (linkMode === 'subscription' && (!selectedUid || !selectedProduct || !selectedPlan)) {
      setError('Please select a user, product, and plan');
      return;
    }
    if (linkMode === 'custom' && !hasValidCustomAmount) {
      setError('Enter a valid custom amount');
      return;
    }

    setGenerating(true);
    setError(null);
    setGeneratedUrl(null);

    try {
      const payload: any = {
        mode: linkMode,
        targetUid: selectedUid || undefined,
        notes: notes || undefined,
      };
      if (linkMode === 'subscription') {
        payload.productId = selectedProduct;
        payload.planId = selectedPlan;
        if (hasValidCustomAmount) payload.customAmount = customAmountNum;
      } else {
        payload.customAmount = customAmountNum;
        payload.itemName = itemName || undefined;
      }

      const result = await callAdminGeneratePaymentLink(payload);
      setGeneratedUrl(result.paymentUrl);
      setGeneratedAmount(result.amount);
      setGeneratedMode(result.mode || linkMode);
      setGeneratedTargeted(Boolean(payload.targetUid));
    } catch (err: any) {
      setError(err?.message || 'Failed to generate payment link');
    } finally {
      setGenerating(false);
    }
  };

  const copyUrl = () => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const currentPlans = plans[selectedProduct] || [];

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <motion.div className="glass-effect rounded-2xl p-8 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 dark:text-gray-300">Loading...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <LinkIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Generate Payment Link</h1>
              <p className="text-slate-600 dark:text-gray-400">Create subscription links or open custom amount links</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="glass-effect rounded-2xl shadow-glass p-6 space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
              Link Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setLinkMode('subscription')}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  linkMode === 'subscription'
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'glass-effect text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800/50'
                }`}
              >
                Subscription
              </button>
              <button
                onClick={() => setLinkMode('custom')}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  linkMode === 'custom'
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'glass-effect text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800/50'
                }`}
              >
                Custom One-Time
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
              Target User {linkMode === 'custom' ? '(optional)' : ''}
            </label>
            <select
              value={selectedUid}
              onChange={(e) => setSelectedUid(e.target.value)}
              className="w-full px-4 py-3 rounded-xl glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Any signed-in user (open link)</option>
              {users.map((u) => (
                <option key={u.uid} value={u.uid}>{u.email}</option>
              ))}
            </select>
          </div>

          {linkMode === 'subscription' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                  Product
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {PRODUCTS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedProduct(p.id)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        selectedProduct === p.id
                          ? 'bg-primary-500 text-white shadow-lg'
                          : 'glass-effect text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                  Plan
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {currentPlans.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} - R{p.price}/month</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
              {linkMode === 'subscription'
                ? 'Custom Amount (optional, overrides plan price)'
                : 'Custom Amount (required)'}
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="number"
                min={1}
                max={100000}
                step="0.01"
                placeholder={linkMode === 'subscription' ? 'Leave empty to use plan price' : 'Enter amount'}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="w-full pl-9 pr-4 py-3 rounded-xl glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {linkMode === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
                Item Label (optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Consulting Session, Invoice #123"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">
              Notes (optional)
            </label>
            <textarea
              placeholder="Add a note for this payment..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-xl glass-effect border border-slate-200 dark:border-gray-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </motion.div>
          )}

          <motion.button
            onClick={handleGenerate}
            disabled={generating || !canGenerate}
            whileHover={{ scale: generating ? 1 : 1.02 }}
            whileTap={{ scale: generating ? 1 : 0.98 }}
            className="w-full py-3 px-6 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Generate Payment Link
              </>
            )}
          </motion.button>

          {generatedUrl && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30"
            >
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                  Payment link generated — R{generatedAmount}{generatedMode === 'subscription' ? '/month' : ' once-off'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={generatedUrl}
                  className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 text-sm text-slate-900 dark:text-white font-mono"
                />
                <button
                  onClick={copyUrl}
                  className="px-3 py-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                {generatedTargeted
                  ? 'Share this link with the selected user.'
                  : 'Share this link with any signed-in user.'} It expires in 7 days.
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPaymentLinks;
